"use client";

import React, { useEffect, useState } from "react";
import { Facebook, Instagram, Linkedin, Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        <div className="h-5 w-16 animate-pulse rounded-md bg-muted" />
        <div className="h-5 w-16 animate-pulse rounded-md bg-muted" />
        <div className="h-5 w-16 animate-pulse rounded-md bg-muted" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-1 py-2 font-sans text-sm font-medium text-foreground transition-colors hover:text-primary">
                {item.label}
                <span className="absolute bottom-0 left-0 h-[1px] w-full origin-bottom-right scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:origin-bottom-left group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNavConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4">
        <div className="h-8 w-32 animate-pulse rounded-md bg-muted" />
        <div className="h-8 w-32 animate-pulse rounded-md bg-muted" />
        <div className="h-8 w-32 animate-pulse rounded-md bg-muted" />
      </div>;
  }
  return <nav className="flex flex-col gap-6">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="font-sans text-2xl font-medium text-foreground transition-colors hover:text-primary" onClick={onItemClick}>
            {item.label}
          </Link>
        </div>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const socialLinks = [{
    icon: Facebook,
    href: "https://www.facebook.com/profile.php?id=61588294055628",
    label: "Facebook"
  }, {
    icon: Instagram,
    href: "https://www.instagram.com/familyfirstseniorcarellc/",
    label: "Instagram"
  }, {
    icon: Linkedin,
    href: "https://www.linkedin.com/company/family-first-senior-care-llc/",
    label: "LinkedIn"
  }];
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <>
      <header id="header" className={`sticky top-0 z-40 w-full transition-all duration-300 ${isScrolled ? "border-b border-primary/10 bg-background/80 backdrop-blur-lg" : "bg-background"}`}>
        <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6 lg:px-8">
          <Link to="https://familyfirstassistedliving.org/" className="flex items-center">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586469/img/family_first_senior_care_silver_spring.png" alt="Family First Senior Care - A warm, family-focused small assisted living home in Montgomery County Maryland, providing residential assisted living Silver Spring and assisted living in Silver Spring MD." className="h-7 w-auto object-contain" />
          </Link>

          <div className="hidden items-center gap-8 md:flex">
            <MenuProvider menu_id="42">
              <DesktopNavConsumer />
            </MenuProvider>

            <Button asChild className="bg-secondary text-secondary-foreground hover:bg-secondary/90">
              <Link to="/ScheduleATourPage">Experience Our Family-Like Care</Link>
            </Button>

            <div className="flex items-center gap-4">
              {socialLinks.map((social, i) => {
              const Icon = social.icon;
              return <Link key={social.label} to={social.href} newTab data-index={i} className="text-muted-foreground hover:text-secondary transition-colors duration-200" aria-label={social.label}>
                    <Icon className="size-5" />
                  </Link>;
            })}
            </div>
          </div>

          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu">
            <Menu className="size-6" />
          </Button>
        </div>
      </header>

      {isMobileMenuOpen && <div className="fixed inset-0 z-50 flex flex-col bg-background">
          <div className="flex h-20 items-center justify-between border-b border-primary/10 px-4">
            <Link to="https://familyfirstassistedliving.org/" className="flex items-center" onClick={() => setIsMobileMenuOpen(false)}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586469/img/family_first_senior_care_silver_spring.png" alt="Family First Senior Care - A warm, family-focused small assisted living home in Montgomery County Maryland, providing residential assisted living Silver Spring and assisted living in Silver Spring MD." className="h-7 w-auto object-contain" />
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu">
              <X className="size-6" />
            </Button>
          </div>

          <div className="flex flex-1 flex-col justify-between overflow-y-auto p-6">
            <div className="mt-8">
              <MenuProvider menu_id="42">
                <MobileNavConsumer onItemClick={() => setIsMobileMenuOpen(false)} />
              </MenuProvider>
            </div>

            <div className="mt-12 pb-8">
              <Button asChild size="lg" className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90">
                <Link to="/ScheduleATourPage" onClick={() => setIsMobileMenuOpen(false)}>
                  Experience Our Family-Like Care
                </Link>
              </Button>
            </div>
          </div>
        </div>}
    </>;
}